
import React, { useState, useContext, useEffect } from 'react'
import {
	Button,
	Card,
	CardBody,
	Col,
	Modal,
	Row,
	Table
} from 'reactstrap'
import { useTranslation } from 'react-i18next'
import { SpinnerComponent } from 'react-element-spinner'

import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
dayjs.extend(localizedFormat)
dayjs.extend(utc)
dayjs.extend(timezone)

import AuctionTabsService from '../../services/AuctionTabsService'
import SiteService from '../../services/SiteService'
import { SessionContext } from '../../contexts/SessionProvider'

/**
 * BidDetailsModal - Shows all bids with bidder info for an auction.
 * BRD: "When you click on details a window should open indicating date/time,
 * iteration bid was placed, amount and dealer name. It should also show the
 * history - if there was a previous bid, it should show the highest bid for
 * each day/iteration if there was one."
 */
const BidDetailsModal = ({ isOpen = false, auction = null, onClose }) =>
{
	const [isModalBusy, setIsModalBusy] = useState(false)
	const { t } = useTranslation()
	const sessionContext = useContext(SessionContext)
	const [bidEntries, setBidEntries] = useState([])

	useEffect(() =>
	{
		if (isOpen && auction?.auctionId)
		{
			retrieveBidDetails()
		}
	}, [isOpen, auction?.auctionId])

	const retrieveBidDetails = () =>
	{
		setIsModalBusy(true)
		AuctionTabsService
			.getBidDetails(auction.auctionId, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsModalBusy(false)
				if (result.data?.loginSession)
				{
					sessionContext.updateSession(result.data.loginSession)
				}
				// Handle various response shapes
				const entries = result.data?.actionResult || result.data?.bids || result.data || []
				setBidEntries(Array.isArray(entries) ? entries : [])
			})
			.catch((error) =>
			{
				setIsModalBusy(false)
				SiteService.displayApiError(error)
				setBidEntries([])
			})
	}

	const handleCloseModal = () =>
	{
		setBidEntries([])
		if (typeof onClose === 'function')
		{
			onClose()
		}
	}

	const formatDate = (dateValue) =>
	{
		if (!dateValue) return '—'
		return dayjs(dateValue).format('MM/DD/YYYY h:mm A')
	}

	const formatCurrency = (value) =>
	{
		if (value === null || value === undefined) return '—'
		return `$${Number(value).toLocaleString()}`
	}

	const vehicleDescription = auction?.vehicleDescription || 
		`${auction?.year || auction?.modelYear || ''} ${auction?.make || ''} ${auction?.model || ''}`.trim() || 
		'Selected Vehicle'

	// Calculate summary stats
	const totalBids = bidEntries.length
	const uniqueBidders = [...new Set(bidEntries.map(b => b.dealerId || b.bidderId))].length
	const highBid = bidEntries.length > 0 ? Math.max(...bidEntries.map(b => b.bidAmount || b.amount || 0)) : 0

	return (
		<Modal
			className="modal-dialog-centered"
			size="lg"
			isOpen={isOpen}
		>
			<SpinnerComponent
				loading={isModalBusy}
				position="centered"
				message="Loading bid details..."
			/>
			<Card className="mb-0">
				<CardBody>
					<div className="text-center text-muted mb-4">
						<h5>Bid Details</h5>
						<small>{vehicleDescription}</small>
						{auction?.vin && <div className="mt-1"><small className="text-muted">VIN: {auction.vin}</small></div>}
					</div>
					
					{/* Summary Stats */}
					<div className="d-flex justify-content-around mb-3 p-2 bg-light rounded">
						<div className="text-center">
							<div className="fw-bold">{totalBids}</div>
							<small className="text-muted">Total Bids</small>
						</div>
						<div className="text-center">
							<div className="fw-bold">{uniqueBidders}</div>
							<small className="text-muted">Unique Bidders</small>
						</div>
						<div className="text-center">
							<div className="fw-bold text-success">{formatCurrency(highBid)}</div>
							<small className="text-muted">High Bid</small>
						</div>
					</div>
					
					{bidEntries.length > 0 ? (
						<div className="table-responsive" style={{ maxHeight: '350px', overflowY: 'auto' }}>
							<Table striped bordered size="sm">
								<thead className="bg-light" style={{ position: 'sticky', top: 0 }}>
									<tr>
										<th>Date/Time</th>
										<th>Iteration</th>
										<th className="text-end">Bid Amount</th>
										<th>Dealer Name</th>
										<th>Dealer Code</th>
									</tr>
								</thead>
								<tbody>
									{bidEntries.map((entry, index) => (
										<tr key={entry.bidId || index} className={index === 0 ? 'table-success' : ''}>
											<td className="text-nowrap" style={{ fontSize: '0.85rem' }}>
												{formatDate(entry.bidDate || entry.createdDate || entry.date)}
											</td>
											<td style={{ fontSize: '0.85rem' }}>
												{entry.iterationName || entry.iteration || '—'}
											</td>
											<td className="text-end fw-bold" style={{ fontSize: '0.85rem' }}>
												{formatCurrency(entry.bidAmount || entry.amount)}
												{index === 0 && <span className="badge bg-success ms-1">High</span>}
											</td>
											<td style={{ fontSize: '0.85rem' }}>
												{entry.dealerName || entry.bidderName || '—'}
											</td>
											<td style={{ fontSize: '0.85rem' }}>
												{entry.dealerCode || entry.bidderCode || '—'}
											</td>
										</tr>
									))}
								</tbody>
							</Table>
						</div>
					) : !isModalBusy && (
						<div className="text-center text-muted py-4">
							<p>No bids have been placed on this auction.</p>
						</div>
					)}
					
					<Row className="mt-4">
						<Col className="text-end">
							<Button
								color="secondary"
								onClick={handleCloseModal}
							>
								Close
							</Button>
						</Col>
					</Row>
				</CardBody>
			</Card>
		</Modal>
	)
}

export default BidDetailsModal
